'use client'

import type { Options } from 'highcharts'

// Static base options — no theme/colour inputs remain (charts render in styled
// mode; colours come from charts.css). Hoisted to a module constant so it is built
// once and shared as a stable reference; `mergeOptions` (Highcharts `merge` without
// a leading `true`) never mutates its sources, so sharing this base is safe.
const HIGHCHARTS_BASE_OPTIONS: Options = {
  title: {
    text: ''
  },
  plotOptions: {
    series: {
      connectNulls: true,
      marker: {
        enabled: false,
        symbol: 'circle',
        width: 13,
        height: 13,
        lineWidth: 2
      },
      dataLabels: {
        style: {
          fontWeight: '400',
          fontFamily: 'FK Grotesk, sans-serif',
          fontSize: '13px'
        }
      }
    },
    area: {
      lineWidth: 2.5,
      showInLegend: false
    },
    areaspline: {
      lineWidth: 2.5,
      showInLegend: true,
      threshold: null,
      legendSymbol: 'rectangle'
    },
    bar: {
      borderWidth: 2
    }
  },
  tooltip: {
    enabled: true,
    padding: 0,
    style: {
      fontFamily: 'FK grotesk, sans-serif',
      fontWeight: '400',
      fontSize: '13px'
    },
    shared: true,
    useHTML: true
  },
  xAxis: {
    maxPadding: 0,
    minPadding: 0,
    type: 'datetime',
    tickLength: 5,
    tickPosition: 'outside',
    dateTimeLabelFormats: {
      hour: '%e %b',
      day: '%e %b',
      week: '%e %b',
      month: "%b '%y",
      year: '%Y'
    },
    labels: {
      enabled: true,
      style: {
        fontFamily: 'FK grotesk, sans-serif',
        fontSize: '11px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal'
      }
    }
  },
  yAxis: {
    opposite: true,
    showFirstLabel: false,
    showLastLabel: false,
    labels: {
      align: 'right',
      x: -10,
      style: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        textOverflow: 'none',
        fontFamily: 'FK Grotesk, sans-serif',
        fontSize: '11px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal'
      }
    },
    title: {
      text: null
    }
  },

  chart: {
    // Styled mode: every colour comes from charts.css via @repo CSS vars
    // (the `.hc-styled-chart` container + the `--color-*` palette).
    styledMode: true,
    plotBorderWidth: 0.5,
    margin: [1, 1, 30, 1]
  },
  legend: {
    enabled: false,
    floating: true,
    verticalAlign: 'top',
    align: 'left',
    borderRadius: 5,
    symbolRadius: 2
  },
  credits: {
    enabled: false
  },
  accessibility: {
    enabled: false
  }
}

// The chart palette as CSS-var references (no colour VALUES in TS). In styled mode
// series are coloured by charts.css (`.highcharts-color-N` → `var(--series-N)`);
// these vars are the same source, so any inline `color`/`fillColor` a chart still
// passes is inert-but-consistent. Kept as a stable module constant.
const SERIES_VARS = Array.from({ length: 10 }, (_, i) => `var(--series-${i})`)
const PALETTE = {
  colors: SERIES_VARS,
  areaChartColors: SERIES_VARS,
  pieChartColors: SERIES_VARS
} as const

export function useHighchartsOptions() {
  return { options: HIGHCHARTS_BASE_OPTIONS, palette: PALETTE }
}
